<template>
  <DomainNotFound v-if="isDomainNotRegistered" />
  <NuxtLayout
    v-else
    name="blank"
  >
    <section
      class="fast-error-page tw-relative tw-isolate tw-flex tw-min-h-screen tw-items-center tw-overflow-hidden tw-px-4 tw-py-10 md:tw-px-8"
    >
      <div class="fast-error-page__grid tw-container tw-relative tw-z-10 tw-grid tw-w-full tw-items-center tw-gap-10 lg:tw-grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)] lg:tw-gap-16">
        <div class="tw-order-2 tw-text-center lg:tw-order-1 lg:tw-text-left">
          <div class="fast-error-page__badge tw-mx-auto tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-mb-button md:tw-text-dt-button lg:tw-mx-0">
            <QIcon
              name="search_off"
              size="20px"
            />
            <span>{{ statusLabel }}</span>
          </div>

          <h1 class="tw-mt-6 tw-text-mb-title-1 md:tw-text-dt-xxl tw-text-black">
            {{ pageTitle }}
          </h1>
          <p class="tw-mx-auto tw-mt-5 tw-max-w-[560px] tw-text-mb-body md:tw-text-dt-body tw-text-dark-gray lg:tw-mx-0">
            {{ pageDescription }}
          </p>

          <div class="tw-mt-8 tw-flex tw-flex-col tw-items-center tw-gap-3 sm:tw-flex-row sm:tw-justify-center lg:tw-justify-start">
            <ButtonPrimary
              :label="$t('global.backToHome')"
              icon="home"
              size="lg"
              class="fast-error-page__primary-button"
              @click="goHome"
            />
            <ButtonPrimary
              v-if="isNotFound"
              :label="$t('global.contactUs')"
              icon="support_agent"
              color="secondary"
              outline
              size="lg"
              class="fast-error-page__secondary-button"
              @click="goContact"
            />
          </div>
        </div>

        <div class="tw-order-1 lg:tw-order-2">
          <div class="fast-error-page__visual tw-relative tw-mx-auto tw-flex tw-aspect-square tw-w-full tw-max-w-[500px] tw-items-center tw-justify-center md:tw-max-w-[580px]">
            <div class="fast-error-page__orbit fast-error-page__orbit--outer" />
            <div class="fast-error-page__orbit fast-error-page__orbit--inner" />
            <QImg
              src="/images/error.webp"
              :alt="statusLabel"
              class="fast-error-page__image"
              fit="contain"
            />
            <div
              v-if="error.statusCode"
              class="fast-error-page__status tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-[64%] tw-font-bold"
            >
              {{ error.statusCode }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="fast-error-page__mark fast-error-page__mark--top"
        aria-hidden="true"
      >
        {{ error.statusCode || 500 }}
      </div>
      <div
        class="fast-error-page__mark fast-error-page__mark--bottom"
        aria-hidden="true"
      >
        {{ error.statusCode || 500 }}
      </div>
      <div
        class="fast-error-page__corner fast-error-page__corner--top"
        aria-hidden="true"
      />
      <div
        class="fast-error-page__corner fast-error-page__corner--bottom"
        aria-hidden="true"
      />
      <div class="fast-error-page__footer tw-absolute tw-inset-x-0 tw-bottom-5 tw-z-10 tw-flex tw-justify-center tw-px-4">
        <NuxtLink
          :to="localePath('/')"
          class="tw-text-mb-body-small md:tw-text-dt-body-small tw-text-dark-gray link-underline-hover"
          @click="clearCurrentError"
        >
          {{ websiteName }}
        </NuxtLink>
      </div>
    </section>
  </NuxtLayout>
</template>

<script setup lang="ts">
interface ErrorPageProps {
  error: {
    statusCode?: number
    statusMessage?: string
    message?: string
  }
}

const props = defineProps<ErrorPageProps>()
const { t } = useI18n()
const localePath = useLocalePath()
const websiteInfoStore = useWebsiteInfoStore()
const contactStore = useContactStore()
const { getPath } = useNavigationMenu()
const { getLineLink } = useSocialLink()

const isNotFound = computed(() => props.error?.statusCode === 404)
const websiteName = computed(() => websiteInfoStore.name || 'WebFaster')

const isDomainNotRegistered = computed(() =>
  props.error?.statusCode === 404 &&
  props.error?.statusMessage === 'Domain not registered',
)

const statusLabel = computed(() =>
  isNotFound.value ? t('errorPage.notFound.eyebrow') : `${props.error?.statusCode || ''}`,
)

const pageTitle = computed(() =>
  isNotFound.value ? t('errorPage.notFound.title') : t('global.somethingWentWrong'),
)

const pageDescription = computed(() =>
  isNotFound.value ? t('errorPage.notFound.description') : props.error?.message,
)

const clearCurrentError = () => {
  clearError()
}

const goHome = () => {
  clearError({ redirect: localePath('/') })
}

const goContact = () => {
  if (contactStore.lineId) {
    window.open(getLineLink(contactStore.lineId), '_blank', 'noopener')
    return
  }

  clearError({ redirect: localePath(getPath({ componentName: 'ContactUs' })) })
}

useHead(() => ({
  title: pageTitle.value,
  meta: isNotFound.value
    ? [
        { name: 'robots', content: 'noindex, follow' },
      ]
    : [],
  link: [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Kanit:wght@400;500;600;700&display=swap' },
  ],
}))
</script>

<style scoped>
.fast-error-page {
  background:
    radial-gradient(circle at 15% 20%, color-mix(in srgb, var(--theme-color-primary) 16%, transparent) 0 18%, transparent 32%),
    radial-gradient(circle at 82% 18%, color-mix(in srgb, var(--theme-color-secondary) 14%, transparent) 0 16%, transparent 34%),
    linear-gradient(135deg, var(--theme-color-white) 0%, var(--theme-color-light-gray-1) 100%);
}

.fast-error-page::before {
  position: absolute;
  inset: 18px;
  z-index: -1;
  border: 1px solid color-mix(in srgb, var(--theme-color-primary) 18%, transparent);
  border-radius: 28px;
  content: "";
}

.fast-error-page__badge {
  color: var(--theme-color-primary);
  background: color-mix(in srgb, var(--theme-color-primary) 10%, var(--theme-color-white));
  border: 1px solid color-mix(in srgb, var(--theme-color-primary) 22%, transparent);
}

.fast-error-page__visual {
  border-radius: 999px;
  background:
    linear-gradient(145deg, color-mix(in srgb, var(--theme-color-white) 86%, transparent), color-mix(in srgb, var(--theme-color-light-gray-2) 58%, transparent)),
    radial-gradient(circle, color-mix(in srgb, var(--theme-color-primary) 8%, transparent), transparent 64%);
  box-shadow: 0 30px 80px color-mix(in srgb, var(--theme-color-shadow-2) 28%, transparent);
}

.fast-error-page__image {
  width: min(78%, 440px);
  filter: drop-shadow(0 24px 40px color-mix(in srgb, var(--theme-color-shadow-2) 24%, transparent));
}

.fast-error-page__status {
  color: var(--theme-color-primary);
  font-size: clamp(3.5rem, 9vw, 8rem);
  line-height: 1;
  text-shadow: 0 10px 22px color-mix(in srgb, var(--theme-color-white) 84%, transparent);
}

.fast-error-page__orbit {
  position: absolute;
  border: 1px solid color-mix(in srgb, var(--theme-color-primary) 20%, transparent);
  border-radius: 999px;
  pointer-events: none;
}

.fast-error-page__orbit--outer {
  inset: 7%;
}

.fast-error-page__orbit--inner {
  inset: 18%;
  border-color: color-mix(in srgb, var(--theme-color-secondary) 24%, transparent);
  transform: rotate(-12deg);
}

.fast-error-page__mark {
  position: absolute;
  z-index: 0;
  color: color-mix(in srgb, var(--theme-color-primary) 8%, transparent);
  font-size: clamp(7rem, 22vw, 20rem);
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.fast-error-page__mark--top {
  top: -0.18em;
  right: 2vw;
}

.fast-error-page__mark--bottom {
  bottom: -0.2em;
  left: 2vw;
}

.fast-error-page__corner {
  position: absolute;
  width: 96px;
  height: 96px;
  border-color: color-mix(in srgb, var(--theme-color-secondary) 34%, transparent);
  pointer-events: none;
}

.fast-error-page__corner--top {
  top: 18px;
  left: 18px;
  border-top: 2px solid;
  border-left: 2px solid;
  border-top-left-radius: 28px;
}

.fast-error-page__corner--bottom {
  right: 18px;
  bottom: 18px;
  border-right: 2px solid;
  border-bottom: 2px solid;
  border-bottom-right-radius: 28px;
}

.fast-error-page__primary-button,
.fast-error-page__secondary-button {
  min-width: 164px;
}

.fast-error-page__primary-button {
  min-height: 48px;
  border-radius: 999px;
  background: var(--theme-color-primary) !important;
  color: var(--theme-color-white) !important;
  box-shadow: 0 14px 30px color-mix(in srgb, var(--theme-color-primary) 32%, transparent);
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.fast-error-page__primary-button :deep(.q-btn__content) {
  color: var(--theme-color-white);
}

.fast-error-page__primary-button:hover {
  background: var(--theme-color-primary-hover) !important;
  box-shadow: 0 18px 38px color-mix(in srgb, var(--theme-color-primary) 40%, transparent);
  transform: translateY(-1px);
}

.fast-error-page__primary-button:active {
  transform: translateY(0);
}

.fast-error-page__secondary-button {
  min-height: 48px;
  border: 1px solid var(--theme-color-secondary) !important;
  border-radius: 999px;
  background: color-mix(in srgb, var(--theme-color-secondary) 8%, var(--theme-color-white)) !important;
  color: var(--theme-color-secondary) !important;
  box-shadow: 0 10px 24px color-mix(in srgb, var(--theme-color-secondary) 16%, transparent);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
}

.fast-error-page__secondary-button :deep(.q-btn__content) {
  color: var(--theme-color-secondary);
}

.fast-error-page__secondary-button:hover {
  border-color: var(--theme-color-secondary-hover) !important;
  background: var(--theme-color-secondary) !important;
  box-shadow: 0 16px 34px color-mix(in srgb, var(--theme-color-secondary) 28%, transparent);
  color: var(--theme-color-white) !important;
  transform: translateY(-1px);
}

.fast-error-page__secondary-button:hover :deep(.q-btn__content) {
  color: var(--theme-color-white);
}

.fast-error-page__secondary-button:active {
  transform: translateY(0);
}

@media (max-width: 767px) {
  .fast-error-page {
    padding-bottom: 72px;
  }

  .fast-error-page::before {
    inset: 10px;
    border-radius: 20px;
  }

  .fast-error-page__corner {
    width: 56px;
    height: 56px;
  }

  .fast-error-page__corner--top {
    top: 10px;
    left: 10px;
    border-top-left-radius: 20px;
  }

  .fast-error-page__corner--bottom {
    right: 10px;
    bottom: 10px;
    border-bottom-right-radius: 20px;
  }
}
</style>
